import {
  type FavoriteVehicle,
  type FinanceInfo,
  type InventoryVehicle,
  type TradeInfo
} from '@carvana/merch-contracts';
import {
  type LoadableState,
  useEffectWithController,
  useLoadableState
} from '@carvana/merch-react-hooks';
import { type AxiosInstance } from 'axios';
import { useRef } from 'react';
import { inventory } from './constants/apis';
import type { InventoryResponse } from './contracts';
import buildFavoritesSearchRequest, {
  type FavoritesSearchAnalytics
} from './utils/buildFavoritesSearchRequest';
import mergeFavoritesWithSearchResults from './utils/mergeFavoritesWithSearchResults';

type OwnArgs = {
  analyticsData: FavoritesSearchAnalytics;

  browserCookieId: string;

  /**
   * Deployment environment
   */
  environment: 'dev' | 'test' | 'prod';

  /**
   * List of favorite vehicles to hydrate from search results
   */
  favoriteVehicles: FavoriteVehicle[];

  /**
   * Axios instance with authentication headers set
   */
  fetcherWithAuth: AxiosInstance;

  /**
   * Whether the user has applied a trade-in vehicle
   */
  hasAppliedTrade?: boolean;

  /**
   * If provided, this loadable state will be used instead of internal state
   */
  inventoryVehicles?: LoadableState<InventoryVehicle[]>;

  /**
   * Callback for handling errors
   */
  onError?: (err: unknown) => void;

  /**
   * Callback fired when finance info is fetched
   */
  onFetchedFinanceInfo?: (financeInfo: FinanceInfo) => void;

  /**
   * Callback fired when trade info is fetched
   */
  onFetchedTradeInfo?: (tradeInfo?: TradeInfo) => void;

  /**
   * The preferred acquisition identifier
   */
  preferredTrade?: string;

  /**
   * If false, the fetch will be skipped (defaults to true)
   */
  shouldFetch?: boolean;

  zip: string;
};

export default function useHydrateFavoritesFromSearch(
  args: OwnArgs
): LoadableState<InventoryVehicle[]> {
  const {
    fetcherWithAuth: { post },
    onError,
    onFetchedFinanceInfo,
    onFetchedTradeInfo
  } = args;

  const onErrorRef = useRef(onError);
  const onFetchedFinanceInfoRef = useRef(onFetchedFinanceInfo);
  const onFetchedTradeInfoRef = useRef(onFetchedTradeInfo);

  // Keep refs up to date
  onErrorRef.current = onError;
  onFetchedFinanceInfoRef.current = onFetchedFinanceInfo;
  onFetchedTradeInfoRef.current = onFetchedTradeInfo;

  const inventoryVehiclesFallback = useLoadableState<InventoryVehicle[]>([]);
  const inventoryVehicles = args.inventoryVehicles ?? inventoryVehiclesFallback;
  const { setData, setError, setLoading } = inventoryVehicles;

  const analyticsDataRef = useRef(args.analyticsData);
  analyticsDataRef.current = args.analyticsData;

  useEffectWithController(
    controller => {
      if (args.shouldFetch === false) {
        return;
      }

      if (args.favoriteVehicles.length === 0) {
        setData([]);

        return;
      }

      setLoading();

      post<InventoryResponse>(
        inventory(args.environment),
        buildFavoritesSearchRequest({
          analyticsData: analyticsDataRef.current,
          browserCookieId: args.browserCookieId,
          favoriteVehicles: args.favoriteVehicles,
          hasAppliedTrade: args.hasAppliedTrade,
          preferredTrade: args.preferredTrade,
          zip: args.zip
        }),
        { signal: controller.signal }
      )
        .then(res => {
          const {
            financeInfo,
            inventory: { vehicles },
            tradeInfo
          } = res.data;

          onFetchedFinanceInfoRef.current?.(financeInfo);
          onFetchedTradeInfoRef.current?.(tradeInfo);

          setData(
            mergeFavoritesWithSearchResults(args.favoriteVehicles, vehicles)
          );
        })
        .catch(err => {
          setError(err);
          onErrorRef.current?.(err);
        });
    },
    [
      args.browserCookieId,
      args.environment,
      args.favoriteVehicles,
      args.hasAppliedTrade,
      args.preferredTrade,
      args.shouldFetch,
      args.zip,
      post,
      setData,
      setError,
      setLoading
    ]
  );

  return inventoryVehicles;
}
